import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Editor, EditorState, RichUtils, convertToRaw, convertFromHTML, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import 'draft-js/dist/Draft.css';

import { __t } from '../../../utils/t';

import './styles.scss';
import { Icon } from '../../Icon';

const customStyleMap = {
    BOLD: {
        fontWeight: '500',
    },
};

const INLINE_STYLES = [
    { icon: 'bold-solid', style: 'BOLD' },
    { icon: 'italic-solid', style: 'ITALIC' },
    { icon: 'underline-solid', style: 'UNDERLINE' },
];
const BLOCK_TYPES = [
    { icon: 'list-ol-solid', style: 'ordered-list-item' },
    { icon: 'list-ul-solid', style: 'unordered-list-item' },
];

interface ToolBarProps {
    editorState: EditorState;
    onChangeHandler: (editorState: EditorState) => void;
}

const ToolBar = ({ editorState, onChangeHandler }: ToolBarProps) => {
    const currentStyle = editorState.getCurrentInlineStyle();
    const selection = editorState.getSelection();
    const blockType = editorState.getCurrentContent().getBlockForKey(selection.getStartKey()).getType();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inlineStyleHandler = (e: any, style: string): void => {
        e.preventDefault();
        onChangeHandler(RichUtils.toggleInlineStyle(editorState, style));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const blockTypeHandler = (e: any, style: string): void => {
        e.preventDefault();
        onChangeHandler(RichUtils.toggleBlockType(editorState, style));
    };

    return (
        <div className='app-rich-text-editor__toolbar'>
            {INLINE_STYLES.map((type) => (
                <button
                    key={type.style}
                    className={`app-rich-text-editor__toolbar-button ${
                        currentStyle.has(type.style) ? 'app-rich-text-editor__toolbar-button--active' : ''
                    }`}
                    onMouseDown={(e) => inlineStyleHandler(e, type.style)}
                >
                    <Icon icon={type.icon} />
                </button>
            ))}

            {BLOCK_TYPES.map((type) => (
                <button
                    key={type.style}
                    className={`app-rich-text-editor__toolbar-button ${
                        type.style === blockType ? 'app-rich-text-editor__toolbar-button--active' : ''
                    }`}
                    onMouseDown={(e) => blockTypeHandler(e, type.style)}
                >
                    <Icon icon={type.icon} />
                </button>
            ))}
        </div>
    );
};

interface Props {
    initialValue: string;
    className?: string;
    disabled?: boolean;
    placeholder?: string;
    maxLength?: number;
    error?: string[];
    tabIndex?: number;
    onChange: (value: string) => void;
}

export const RichTextEditor = ({ initialValue, className = '', placeholder, maxLength, error, tabIndex, onChange }: Props) => {
    const editorRef = useRef<Editor | null>(null);
    const inputWrapperRef = useRef<HTMLDivElement | null>(null);
    const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
    const [isFocused, setIsFocused] = useState(false);
    const [isInitialValueSet, setIsInitialValueSet] = useState(false);
    const [symbolCount, setSymbolCount] = useState(0);

    useEffect(() => {
        if (initialValue && !isInitialValueSet) {
            const blocksFromHTML = convertFromHTML(initialValue);
            const initialState = ContentState.createFromBlockArray(
                blocksFromHTML.contentBlocks,
                blocksFromHTML.entityMap,
            );
            setEditorState(EditorState.createWithContent(initialState));
            setIsInitialValueSet(true);
        }
    }, [initialValue]);

    const onChangeHandler = (editorState: EditorState): void => {
        if (!isInitialValueSet) {
            setIsInitialValueSet(true);
        }

        setEditorState(editorState);
        const rawTextLength = editorState.getCurrentContent().getPlainText('').length;
        if (rawTextLength) {
            const rawContentState = convertToRaw(editorState.getCurrentContent());
            const markup = draftToHtml(rawContentState);
            onChange(markup);
        } else {
            onChange('');
        }

        setSymbolCount(rawTextLength);
    };

    const onFocusHandler = () => setIsFocused(true);

    const onBlurHandler = () => setIsFocused(false);

    const setFocus = () => editorRef.current?.focus();

    const symbolCounter = useMemo(() => {
        if (!maxLength || (maxLength && !symbolCount)) {
            return null;
        }

        return (
            <div className='app-rich-text-editor__symbol-count'>
                {`${__t('осталось')} ${maxLength - symbolCount} ${__t('символов')}`}
            </div>
        );
    }, [symbolCount, maxLength]);

    // If the user changes block type before entering any text, we can
    // either style the placeholder or hide it. Let's just hide it now.
    let placeholderVisibilityClassName = '';
    const contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
        if (contentState.getBlockMap().first().getType() !== 'unstyled') {
            placeholderVisibilityClassName = ' RichEditor-hidePlaceholder';
        }
    }

    return (
        <div className={`app-rich-text-editor ${className}`}>
            <div
                className={`app-rich-text-editor__container ${
                    isFocused ? 'app-rich-text-editor__container--focused' : ''
                } ${error ? 'app-rich-text-editor__container--has-error' : ''}`}
            >
                <div
                    className={`app-rich-text-editor__input-wrapper ${placeholderVisibilityClassName}`}
                    ref={inputWrapperRef}
                    onClick={setFocus}
                >
                    <Editor
                        editorState={editorState}
                        placeholder={placeholder}
                        customStyleMap={customStyleMap}
                        ref={editorRef}
                        tabIndex={tabIndex}
                        onChange={onChangeHandler}
                        onFocus={onFocusHandler}
                        onBlur={onBlurHandler}
                    />
                </div>

                <ToolBar editorState={editorState} onChangeHandler={onChangeHandler} />
            </div>

            {!!error &&
                error.length &&
                error.map((e, i) => (
                    <span className='app-rich-text-editor__error-message' key={i}>
                        {e}
                    </span>
                ))}

            {symbolCounter}
        </div>
    );
};
